/** The lap comparison: what the logger's `/api/analysis/compare` answers,
 *  worked out in the browser from the lap documents instead.
 *
 * Distance-resampled series for each lap, the time delta against the
 * reference, the reference's corners and every lap's report card through
 * them, the accelerometer calibration and each lap's g-g envelope. The
 * output keeps the endpoint's keys exactly, so the logger's own UI and this
 * portal read one shape.
 */

import { accelCalibration, ggExtremes } from './accel';
import { alignToReference, ReferencePath, remapEvents } from './alignment';
import { has } from './columns';
import { LapTrace, measure, windows } from './cornerMetrics';
import { cornerReport } from './cornerReport';
import { cornersForLap } from './corners';
import { timeDeltaSeries } from './delta';
import { speedPeaksValleys } from './peaks';
import { resampleByDistance, resampleByTime } from './resample';
import type { AuthoredCorner, CompareLap, CompareResult, LapEvent, Samples } from './types';

/** Default channel set — the pre-Tier-1 payload, so clients that never open
 *  the newer channels pay nothing extra. */
export const COMPARE_COLUMNS: readonly string[] = [
  't', 'speed', 'throttle', 'brake', 'coast', 'gear', 'rpm', 'boost', 'tire_slip', 'yaw_rate',
  'pos_x', 'pos_z',
];

/** Time sync on the race-line map (#75) places each lap's car by its own
 *  clock, from this track rather than the distance series: 20 Hz is a car
 *  length at racing speed, and a jump (GT7 resetting the car) blurs for
 *  1/20 s at most. */
export const TRACK_STEP_S = 0.05;

/** Columns not every recording can fill. A lap that did not carry one from
 *  start to finish has no such channel at all (see the logger's
 *  `prune_optional`): a zero-filled steering trace would read as "the
 *  driver never turned". */
export const OPTIONAL_COLUMNS: readonly string[] = [
  'steer',
  'acc_lat', 'acc_long', 'acc_vert',
  'throttle_f', 'brake_f',
  'race_pos',
  'body_slip',
  'wheel_angle', // the front road wheels' mean angle, rad (packet C)
];

/** Columnar per-tick series the logger keeps for each lap, in its order. */
export const SAMPLE_COLUMNS: readonly string[] = [
  't', 'dist', 'speed', 'throttle', 'brake', 'coast', 'gear', 'rpm',
  'boost', 'tire_slip', 'yaw_rate', 'pos_x', 'pos_z', 'body_height', 'fuel',
  // Tier 1 per-corner channels (FL FR RL RR)
  'slip_fl', 'slip_fr', 'slip_rl', 'slip_rr',
  'tt_fl', 'tt_fr', 'tt_rl', 'tt_rr',
  'sus_fl', 'sus_fr', 'sus_rl', 'sus_rr', // suspension compression, mm
  'aids', // AidsBits mask: TCS | ASM | handbrake | rev limiter
  'surface', // packed per-wheel surface codes
  'pos_y', // elevation beside pos_x/pos_z (#96); laps stored before it have none
  // GT7's suggested gear (#108), 15 where it suggested nothing. Like pos_y it
  // is not optional in the prune_optional sense, but a lap on which GT7
  // suggested nothing has none, and neither do laps stored before it.
  'suggested_gear',
  ...OPTIONAL_COLUMNS,
];

/** Columns a comparison may ask for beyond the defaults. */
export const EXTRA_COMPARE_COLUMNS: readonly string[] = SAMPLE_COLUMNS.filter(
  (c) => !COMPARE_COLUMNS.includes(c) && c !== 'dist',
);

/** The comparison's channel list from what a caller asked for: the delta
 *  and the race-line map always need `t`, `pos_x` and `pos_z`. */
export function compareColumns(requested?: readonly string[] | null): string[] {
  if (requested === undefined || requested === null) return [...COMPARE_COLUMNS];
  return [...new Set(['t', 'pos_x', 'pos_z', ...requested.filter((c) => c.trim() !== '')])];
}

/** Distance-resampled series for each lap + time delta vs the reference.
 *
 * `samplesById` and `eventsById` are the lap documents' `samples` and
 * `events` by lap id; `ref` must be among them. `columns` is the channel
 * list (see `compareColumns`), `step` the distance grid in metres and
 * `authored` the circuit's authored corners, or an empty list to detect
 * them on the reference lap.
 */
export function compareLaps(
  samplesById: ReadonlyMap<number, Samples>,
  eventsById: ReadonlyMap<number, LapEvent[]>,
  ref: number,
  columns: readonly string[],
  step: number,
  authored: readonly AuthoredCorner[],
): CompareResult {
  const refSamples = samplesById.get(ref);
  if (refSamples === undefined) throw new Error(`reference lap ${ref} is not among the laps`);
  // Corner numbering comes from the reference lap only, so every overlaid
  // lap shares one consistent set of map markers — and from the circuit's
  // authored corners when it has them, so the numbering is the same in
  // every session too, not just within this one.
  const refCorners = cornersForLap(refSamples, authored);
  // Every other lap is put on the reference's distance axis by WHERE it
  // was, not how far it had gone (alignment.ts): the delta, the charts, the
  // corner report and the map's position sync then compare the laps at the
  // same place on track. A lap that cannot be lined up keeps its own
  // distance and says so (`aligned: false`).
  const path = new ReferencePath(refSamples);
  // What the reference did at each corner, which is what every other lap's
  // braking point is early or late against (#110).
  const refMeasures = measure(new LapTrace(refSamples), windows(refCorners));

  const out: CompareResult = {
    ref,
    step,
    channels: [...columns],
    // Unit + sign calibration for the broadcast accelerometer, fitted on
    // the REFERENCE lap and applied to every lap in the comparison, so the
    // g-g diagram plots them all on one axis (#16).
    accel: accelCalibration(refSamples),
    laps: {},
  };
  for (const [lapId, raw] of samplesById) {
    let samples = raw;
    let events = eventsById.get(lapId) ?? [];
    let aligned = lapId === ref;
    if (!aligned) {
      const moved = alignToReference(raw, path);
      if (moved !== null) {
        events = remapEvents(events, raw['dist'] ?? [], moved['dist'] ?? []);
        samples = moved;
        aligned = true;
      }
    }
    const present = columns.filter((c) => has(samples, c));
    const entry: CompareLap = {
      series: resampleByDistance(samples, step, present),
      peaks_valleys: speedPeaksValleys(samples),
      events,
      aligned,
      // By the lap's own clock, independent of any alignment.
      track: resampleByTime(raw, TRACK_STEP_S, ['pos_x', 'pos_z']),
    };
    if (out.accel.available && has(samples, 'acc_lat')) {
      // Peaks come from the RAW ticks, not the resampled series the
      // scatter draws: distance resampling smooths exactly the moments a
      // traction-circle readout is about.
      entry.gg = ggExtremes(samples, out.accel);
    }
    if (lapId === ref) {
      entry.corners = refCorners;
    } else {
      entry.delta = timeDeltaSeries(samples, refSamples, step);
    }
    if (refCorners.length > 0) {
      // Every lap measured through the SAME corner windows (the
      // reference's), which is what makes the per-corner report card's
      // time-lost column mean something (#21).
      entry.corner_report =
        lapId === ref
          ? cornerReport(refCorners, samples, { measures: refMeasures })
          : cornerReport(refCorners, samples, { reference: refMeasures });
    }
    out.laps[String(lapId)] = entry;
  }
  return out;
}
